/**
 * API utility for HTTP calls to the backend
 * All endpoints follow the pattern: /api/{resource}/{action}
 * 
 * Uses credentials: 'include' to send httpOnly cookies with requests
 */

import { API_URL } from '../config';
import type { AdminPermissions } from '@swtorbit/shared-socket-events';

export interface RefreshTokenResponse {
  accessToken: string;
  /** Whether the authenticated admin is a super-admin (present on refresh). */
  isSuperAdmin?: boolean;
  /** The admin's own permissions, used to tailor their UI (present on login/refresh). */
  permissions?: AdminPermissions;
}

export interface HealthResponse {
  status: string;
  timestamp: number;
}

/**
 * Refresh access token using httpOnly cookie
 * The refresh token is automatically sent via cookie
 * @endpoint POST /api/auth/refresh
 */
export async function refreshAccessToken(): Promise<RefreshTokenResponse> {
  const response = await fetch(`${API_URL}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include', // Send httpOnly cookies
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}), // Empty body, token is in cookie
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to refresh token' }));
    throw new Error(error.error || 'Failed to refresh token');
  }

  return response.json();
}

/**
 * Logout and clear the httpOnly refresh token cookie
 * @endpoint POST /api/auth/logout
 */
export async function logout(): Promise<void> {
  await fetch(`${API_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include', // Send httpOnly cookies
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

/**
 * Check server health
 * @endpoint GET /api/health
 */
export async function checkHealth(): Promise<HealthResponse> {
  const response = await fetch(`${API_URL}/api/health`);
  
  if (!response.ok) {
    throw new Error('Health check failed');
  }
  
  return response.json();
}
